import type { VFC } from 'react';
import type Highcharts from 'highcharts';
import type { SeriesColumnOptions } from 'highcharts';
import { type AnalysisApiResponse, isLicenseCategory } from '@enmon/commons/lib/enmon';
import dayjs from 'dayjs';
import { LICENSE_CATEGORY_TRANSLATED } from 'common';
import type { GetLicensesAnalysisResponse } from 'lib/services';
import { ChartLoadingSkeleton } from 'components/loading_skeletons';
import { BaseChart } from './Base';
import { localeSort } from '../../utils/string/locale-sort';

const getChartMargin = (series: AnalysisApiResponse['series']): number[] => {
  const monthsCount = Object.values(series?.[0]?.data ?? {}).length;
  const marginBottom = monthsCount > 12 ? 120 : 100;
  // Controlled marginBottom, because Highcharts make category names diagonal when there are more than 12 data points.
  return [100, 40, marginBottom, 70];
};

export const getSerieData = ({
  categories: [categories],
  series,
}: GetLicensesAnalysisResponse): SeriesColumnOptions[] => {
  const sortedSeries = series.sort(({ meta: { category: entityA } }, { meta: { category: entityB } }) =>
    localeSort(entityA, entityB),
  );

  const getData = (dates: string[], data: AnalysisApiResponse['series'][number]['data']) =>
    // -1 because license endpoint return a month for which the usage snapshot was not yet created
    Array.from({ length: dates.length - 1 }, (_, index) => (data[index] ? data[index] : { y: 0 }));

  return sortedSeries.map(serie => ({
    name: isLicenseCategory(serie.meta.category)
      ? LICENSE_CATEGORY_TRANSLATED[serie.meta.category]
      : serie.meta.category,
    data: getData(categories ?? [], serie.data) as SeriesColumnOptions['data'],
    type: 'column',
  }));
};

const getChartOptions = ({ series, categories }: GetLicensesAnalysisResponse): Highcharts.Options => ({
  xAxis: {
    categories: categories[0]?.map(category => category.toString()),
    labels: {
      formatter() {
        return dayjs(this.value).format('MMM YYYY');
      },
    },
  },
  title: {
    text: 'Total Licenses',
    align: 'left',
    margin: 50,
    style: { fontSize: '20px', fontWeight: 'bold' },
    x: 15,
    y: 35,
  },
  yAxis: {
    title: {
      text: '',
    },
  },
  chart: {
    height: 500,
    margin: getChartMargin(series),
  },
  legend: {
    align: 'center',
  },
  colors: ['#1BA39C', '#92DB36', '#29BFE5'],
  tooltip: {
    formatter() {
      return `<b>${this.series.name}:</b> ${this.y?.toString()}`;
    },
  },
  series: getSerieData({ categories, series }),
});

interface LicenseChartProps {
  licenseAnalysisData: GetLicensesAnalysisResponse | undefined;
  isFetching: boolean;
}

export const LicenseChart: VFC<LicenseChartProps> = ({
  licenseAnalysisData: { categories = [[]], series = [] } = {},
  isFetching,
}) => {
  const options = getChartOptions({ series, categories });

  return isFetching ? <ChartLoadingSkeleton /> : <BaseChart options={options} />;
};
